import { useEffect, useState, useRef, Suspense } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { AnnouncementBanner } from '@/components/admin/AnnouncementBanner';
import { useAuth } from '@/lib/auth';
import { SideNav } from './SideNav';
import { AppLoadingScreen } from './AppLoadingScreen';
import { OnboardingFlow } from '@/components/onboarding/OnboardingFlow';


import { useOnboardingState } from '@/hooks/useOnboardingState';
import { useIsMobile } from '@/hooks/use-mobile';
import { ProjectFilterProvider } from '@/contexts/ProjectFilterContext';
import { MobileInterstitial } from './MobileInterstitial';
import { useScrollRestore } from '@/hooks/useScrollRestore';
import { startSessionHeartbeat } from '@/lib/productAnalytics';

export function AppLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  
  const { showOnboarding, onboardingType, isLoading: onboardingLoading, completeOnboarding } = useOnboardingState();
  const isMobile = useIsMobile();
  const location = useLocation();
  const mainContentRef = useRef<HTMLElement>(null);
  
  useScrollRestore(mainContentRef, location.pathname);

  // Crisp chat is mounted as a component (CrispChat) — handles env-flag gating,
  // auth gating, and route-based hiding internally.

  // Check if user's email is verified

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/auth');
      } else if (!user.email_confirmed_at) {
        navigate('/auth');
      } else {
        setIsReady(true);
      }
    }
  }, [user, loading, navigate]);

  // Start product-analytics session + 30-min heartbeat once authenticated.
  useEffect(() => {
    if (!isReady || !user) return;
    return startSessionHeartbeat();
  }, [isReady, user]);

  // Preload all main authenticated route chunks after the shell mounts so
  // tab navigation (Dashboard ↔ Proposals ↔ Vault ↔ Analytics) is instant
  // with no Suspense fallback flash.
  useEffect(() => {
    if (!isReady) return;
    const t = window.setTimeout(() => {
      import("@/lib/preloadRoutes").then((m) => m.preloadAuthedRoutes?.());
    }, 150);
    return () => window.clearTimeout(t);
  }, [isReady]);

  const handleOnboardingComplete = async (projectId?: string) => {
    await completeOnboarding();
    
    // Navigate after a brief delay to allow the reveal animation to start
    setTimeout(() => {
      if (projectId) {
        navigate(`/projects/${projectId}`);
      } else {
        navigate('/dashboard');
      }
    }, 100);
  };

  // Mobile interstitial — gate FIRST, before any auth/onboarding queries.
  // Otherwise mobile users sit on the loading screen waiting for data they'll
  // never use (slow Google-login-on-mobile bounce).
  if (isMobile) {
    return <MobileInterstitial />;
  }

  // Show premium loading screen while auth or onboarding status is loading
  if (loading || onboardingLoading) {
    return <AppLoadingScreen />;
  }

  // If not ready yet (no user, redirecting), show loading
  if (!isReady || !user) {
    return <AppLoadingScreen />;
  }

  // Show full-screen onboarding if user is new or invited member
  if (showOnboarding && onboardingType !== 'none') {
    return <OnboardingFlow onComplete={handleOnboardingComplete} flowType={onboardingType} />;
  }


  return (
    <ProjectFilterProvider>
       <div className="min-h-screen flex w-full bg-background relative">
          {/* Side Navigation */}
          <SideNav />
          
          {/* Main Content */}
          <main ref={mainContentRef} className="flex-1 min-h-0 overflow-auto relative z-[1]">
            <ImpersonationBanner />
            <AnnouncementBanner />
            <div className="px-6 py-8 md:px-8 md:py-10 h-full min-h-0">
              <Suspense fallback={<div className="h-full w-full bg-background" />}>
                <Outlet />
              </Suspense>
            </div>
          </main>
        </div>
    </ProjectFilterProvider>
  );
}
